/* Image-led editorial groups: preserve the full photograph and close group gaps.
 * Image dimensions, including lazy image attributes, determine the geometry.
 * Shopify image_tag continues to supply any merchant-defined focal point.
 */
(() => {
  if (window.pyCollectionFit) { window.pyCollectionFit.scan(); return; }
  const roots = new Map();
  let frame = 0;
  const request = () => { if (!frame) frame = requestAnimationFrame(render); };
  const ratio = cell => {
    const img = cell.querySelector('.py-editorial-card__visual img');
    const width = Number(img?.getAttribute('width')) || img?.naturalWidth;
    const height = Number(img?.getAttribute('height')) || img?.naturalHeight;
    return width > 0 && height > 0 ? width / height : 1;
  };
  const columns = widths => widths.map(w => `minmax(0,${w.toFixed(4)}fr)`).join(' ');
  const px = n => `${Math.max(0,n).toFixed(3)}px`;
  function fit(block, width, captionHeight) {
    const narrow = innerWidth <= 900;
    const gap = narrow ? 16 : 22;
    const meta = captionHeight ?? (parseFloat(getComputedStyle(block).getPropertyValue('--py-caption-space')) || 140);
    const cells = [...block.querySelectorAll('.py-editorial-cell')];
    if (!cells.length || width <= 0) return;
    const ratios = cells.map(cell => {
      const r = ratio(cell);
      cell.querySelector('.py-editorial-card')?.style.setProperty('--py-image-ratio', r);
      cell.style.removeProperty('height');
      cell.style.removeProperty('grid-column');
      return r;
    });
    block.style.setProperty('--py-meta-height', px(meta));
    const primary = block.querySelector('.py-editorial-cell--primary');
    const stack = block.querySelector('.py-editorial-stack');
    if (primary && stack && cells.length === 3) {
      const p = ratio(primary);
      const pair = [...stack.querySelectorAll('.py-editorial-cell')];
      const a = ratio(pair[0]), b = ratio(pair[1]);
      if (narrow) {
        primary.style.height = px(width / p + meta);
        block.style.setProperty('--py-fit-columns', '1fr');
        block.style.removeProperty('--py-fit-height');
        stack.style.setProperty('--py-stack-columns', columns([a,b]));
        stack.style.setProperty('--py-stack-height', px((width-gap)/(a+b)+meta));
        stack.style.removeProperty('--py-stack-rows');
      } else {
        // Equal outer heights: large image + caption = two smaller images + captions.
        const sw = ((width-gap)/p-meta-gap)/(1/p+1/a+1/b);
        const pw = width-gap-sw;
        if (sw <= 0 || pw <= 0) return;
        const right = block.classList.contains('py-editorial-block--feature-right');
        block.style.setProperty('--py-fit-columns', columns(right ? [sw,pw] : [pw,sw]));
        block.style.setProperty('--py-fit-height', px(pw/p+meta));
        stack.style.setProperty('--py-stack-rows', `${px(sw/a+meta)} ${px(sw/b+meta)}`);
        stack.style.removeProperty('--py-stack-columns');
        stack.style.removeProperty('--py-stack-height');
      }
    } else if (narrow && cells.length === 3) {
      cells[0].style.gridColumn = '1 / -1';
      cells[0].style.height = px(width/ratios[0]+meta);
      const h = (width-gap)/(ratios[1]+ratios[2]);
      cells.slice(1).forEach(cell => { cell.style.height = px(h+meta); });
      block.style.setProperty('--py-fit-columns', columns(ratios.slice(1)));
      block.style.removeProperty('--py-fit-height');
    } else {
      const h = (width-gap*(cells.length-1))/ratios.reduce((sum,r)=>sum+r,0);
      block.style.setProperty('--py-fit-columns', columns(ratios));
      block.style.setProperty('--py-fit-height', px(h+meta));
    }
    block.dataset.pyFitted = 'true';
  }
  function render() {
    frame = 0;
    roots.forEach((state,root) => {
      if (!root.isConnected) { state.resize.disconnect(); roots.delete(root); return; }
      if (root.closest('[data-py-view="compact"]')) return;
      const width = root.clientWidth;
      const caption = parseFloat(getComputedStyle(root).getPropertyValue('--py-caption-space')) || 158;
      root.querySelectorAll('.py-editorial-block').forEach(block => fit(block,width,caption));
    });
  }
  const initialized = new WeakSet();
  function initializeControls(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    const wall = root.querySelector('.py-editorial-wall');
    const buttons = root.querySelectorAll('[data-py-view-button]');
    const sort = root.querySelector('[data-py-sort]');
    const activeParams = new URL(location.href).searchParams;
    if (wall && (activeParams.has('filter.v.price.gte') || activeParams.has('filter.v.price.lte')) && ['price-ascending', 'price-descending'].includes(sort?.value)) {
      // Shopify sorts by the product's minimum price. With a range applied,
      // use the matching variant price shown on the card instead.
      const cells = [...wall.querySelectorAll('.py-editorial-cell')];
      const cards = cells.map(cell => cell.querySelector('.py-editorial-card')).filter(Boolean);
      if (cards.length === cells.length) {
        const direction = sort.value === 'price-descending' ? -1 : 1;
        cards.sort((a,b) => direction * (Number(a.dataset.pySortPrice) - Number(b.dataset.pySortPrice)));
        cards.forEach(card => card.remove());
        cells.forEach((cell,index) => {
          const card = cards[index];
          cell.append(card);
          const caption = card.querySelector('.py-editorial-card__meta > small');
          if (caption) caption.textContent = `${String(index+1).padStart(2,'0')} / PUTAOYAO`;
          const img = card.querySelector('.py-editorial-card__image-link > py-progressive-image > img');
          const slot = (index+1) % 9;
          if (img) img.setAttribute('sizes', slot === 1 || slot === 0 ? '(min-width:1400px) 900px, (min-width:901px) 61vw, 94vw' : slot === 4 ? '(min-width:901px) 31vw, 94vw' : '(min-width:1400px) 430px, (min-width:901px) 31vw, 46vw');
        });
      }
    }
    function setView(view) {
      root.dataset.pyView = view === 'compact' ? 'compact' : 'art';
      buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.pyViewButton === root.dataset.pyView)));
      root.querySelectorAll('.py-editorial-card__image-link > py-progressive-image > img').forEach(img => {
        if (!img.dataset.artSizes) img.dataset.artSizes = img.getAttribute('sizes') || '100vw';
        img.setAttribute('sizes', root.dataset.pyView === 'compact' ? '(max-width:620px) 44vw, 180px' : img.dataset.artSizes);
      });
      if (view === 'compact') {
        wall?.querySelectorAll('.py-editorial-cell').forEach(cell => { cell.style.removeProperty('height'); cell.style.removeProperty('grid-column'); });
      }
      request();
    }
    // Match the server-rendered artistic view on every new catalog visit.
    // Compact view remains an explicit choice for this page, never a stored default.
    setView('art');
    buttons.forEach(button => button.addEventListener('click', () => {
      setView(button.dataset.pyViewButton);
    }));
    const form = root.querySelector('[data-py-price-form]');
    if (form) {
      const min = form.elements.namedItem('filter.v.price.gte');
      const max = form.elements.namedItem('filter.v.price.lte');
      const rate = Number(form.dataset.rate) || 1;
      const currency = form.dataset.currency || 'JPY';
      const prefix = form.dataset.prefix || '¥';
      const summary = root.querySelector('[data-py-price-summary]');
      const params = new URL(location.href).searchParams;
      const displayKeys = ['py_price_min', 'py_price_max'];
      [min, max].forEach((input, index) => {
        const nativeValue = params.get(input.name);
        const hasNative = nativeValue !== null && nativeValue !== '';
        const savedValue = params.get(displayKeys[index]);
        input.value = hasNative ? (params.get('py_price_currency') === currency && savedValue !== null ? savedValue : String(Math.round(Number(nativeValue) * rate / 10) * 10)) : '';
        input.addEventListener('input', () => max.setCustomValidity(''));
      });
      function updateSummary() {
        if (summary) summary.textContent = min.value === '' && max.value === '' ? form.dataset.allPrices : `${prefix}${min.value || '0'} – ${max.value || '∞'}`;
      }
      updateSummary();
      function navigate(clear, sortValue) {
        const url = new URL(location.href);
        [min, max].forEach((input, index) => {
          if (clear || input.value === '') {
            url.searchParams.delete(input.name);
            url.searchParams.delete(displayKeys[index]);
          } else {
            // Prices display rounded to tens. Include exactly the native prices
            // that round into the entered display-currency interval.
            const entered = Number(input.value);
            const lower = Math.ceil(entered / 10) * 10;
            const upper = Math.floor(entered / 10) * 10;
            const native = index === 0 ? Math.ceil(Math.max(0, lower - 5) / rate) : Math.ceil((upper + 5) / rate) - 1;
            url.searchParams.set(input.name, String(Math.max(0, native)));
            url.searchParams.set(displayKeys[index], input.value);
          }
        });
        if (clear || min.value === '' && max.value === '') url.searchParams.delete('py_price_currency');
        else url.searchParams.set('py_price_currency', currency);
        if (sortValue !== undefined) {
          if (sortValue) url.searchParams.set('sort_by', sortValue);
          else url.searchParams.delete('sort_by');
        }
        url.searchParams.delete('page');
        if (clear) { min.value = ''; max.value = ''; }
        updateSummary();
        if (window.pyPageTransition?.navigate) window.pyPageTransition.navigate(url.toString());
        else location.href = url.toString();
      }
      form.addEventListener('submit', event => {
        event.preventDefault();
        max.setCustomValidity(min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value) ? form.dataset.rangeError : '');
        if (form.reportValidity()) navigate(false);
      });
      form.querySelector('[data-py-price-reset]')?.addEventListener('click', () => navigate(true));
      sort?.addEventListener('change', () => {
        max.setCustomValidity(min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value) ? form.dataset.rangeError : '');
        if (form.reportValidity()) navigate(false, sort.value);
      });
    }
    function prepareHover(event) {
      if (!matchMedia('(hover:hover) and (pointer:fine)').matches) return;
      const link = event.target.closest?.('.py-editorial-card__image-link');
      const template = link?.querySelector('template[data-py-hover-image]');
      if (!template || link.dataset.hoverPrepared) return;
      link.dataset.hoverPrepared = 'true';
      const image = template.content.querySelector('img').cloneNode(true);
      if (root.dataset.pyView === 'compact') image.sizes = '(max-width:620px) 44vw, 180px';
      image.addEventListener('load', async () => {
        try { await image.decode(); } catch (_) {}
        if (image.naturalWidth) link.classList.add('py-hover-ready');
      }, { once: true });
      link.append(image);
    }
    root.addEventListener('pointerover', prepareHover);
    root.addEventListener('focusin', prepareHover);
  }
  function scan() {
    document.querySelectorAll('.py-editorial-collection').forEach(initializeControls);
    document.querySelectorAll('.py-editorial-wall').forEach(root => {
      if (roots.has(root)) return;
      let lastWidth = -1;
      const resize = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        if (Math.abs(width-lastWidth) > .5) { lastWidth=width; request(); }
      });
      resize.observe(root);
      roots.set(root,{resize});
    });
    request();
  }
  window.pyCollectionFit = {scan};
  addEventListener('resize',request,{passive:true});
  document.addEventListener('shopify:section:load',scan);
  document.addEventListener('shopify:section:unload',request);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',scan,{once:true});
  else scan();
})();
